import type {
  GroupNavItem,
  InterfaceEntity,
  NavItem,
  PagePermissions,
} from '@unifyapps/defs/types/interface';
import { getNavItemWithAdaptedUrl } from '../../../../components/NavigationContainer/utils/getNavItemWithAdaptedUrl';
import type { UserPermissionsMap } from '../../../access-control/types';
import { filterPermissibleNavItems } from '../../../access-control/utils/filterPermissibleNavItems';
import { InterfaceModes } from '../../../stores/InterfaceStore/types';

export type NavItemTransformer = (items: NavItem[]) => NavItem[];

export interface NavItemTransformerOptions {
  mode: InterfaceModes;
  interfaceRecord: InterfaceEntity;
  permissions?: UserPermissionsMap;
  pagePermissions?: PagePermissions;
}

function transformNavItem(item: NavItem, options: NavItemTransformerOptions): NavItem {
  switch (item.type) {
    case 'PAGE':
    case 'MODULE':
    case 'URL': {
      return getNavItemWithAdaptedUrl({
        navItem: item,
        interfaceRecord: options.interfaceRecord,
        mode: options.mode,
      }) as unknown as NavItem;
    }
    case 'GROUP':
      return {
        ...item,
        items: (item as GroupNavItem).items.map((child) => transformNavItem(child, options)),
      } as NavItem;
    default:
      return item;
  }
}

/**
 * Creates a NavItemTransformer that:
 * - In RUNNER mode: filters by permissions and resolves to runner-relative paths (/{slug})
 * - In BUILDER/PREVIEW mode: resolves to builder/preview routes (/interfaces/{id}/builder/{slug})
 */
export function createNavItemTransformer(options: NavItemTransformerOptions): NavItemTransformer {
  return (items: NavItem[]) => {
    const permissions = options.permissions;

    const shouldFilter =
      options.mode === InterfaceModes.RUNNER &&
      permissions &&
      typeof permissions === 'object' &&
      Object.keys(permissions).length > 0;
    const filteredItems = shouldFilter
      ? filterPermissibleNavItems({
          items,
          permissions,
          pagePermissions: options.pagePermissions,
        })
      : items;

    return filteredItems.map((item) => transformNavItem(item, options));
  };
}
